/* eslint-disable no-restricted-syntax, guard-for-in */ import { $address, $token, $tokens } from './constants';
import { hash, isConditionalObject } from '../util';
import { VERSION } from '../version';
export const DEFAULT_KEY = '$default';
/**
 * Returns true if it is a token, false if it's not a token
 */ export function isToken(t) {
    if (t && (t === null || t === void 0 ? void 0 : t[$token])) {
        return true;
    }
    return false;
}
/**
 * Returns true if it is a token map, false if it's not a token map
 */ export function isTokens(t) {
    if (t && (t === null || t === void 0 ? void 0 : t[$tokens])) {
        return true;
    }
    return false;
}
/** @internal */ export function getTokenId(token) {
    var _token_$address;
    const id = (_token_$address = token[$address]) === null || _token_$address === void 0 ? void 0 : _token_$address.id;
    if (id == null) {
        throw new Error('Missing token id');
    }
    return id;
}
export function getTokenData(token) {
    return token[$address];
}
export function getTokenPath(token) {
    var _token_$address;
    const address = (_token_$address = token[$address]) === null || _token_$address === void 0 ? void 0 : _token_$address.path;
    if (address == null) {
        throw new Error('Missing token address');
    }
    return address;
}
export function getTokenMode(token) {
    var _token_$address;
    const mode = (_token_$address = token[$address]) === null || _token_$address === void 0 ? void 0 : _token_$address.mode;
    if (mode == null) {
        throw new Error('Missing token mode');
    }
    return mode;
}
const STARTS_WITH_DIGIT = /^\d/;
export function serializeCustomProperty(id) {
    const prefix = id.match(STARTS_WITH_DIGIT) ? 's-' : '';
    return `--${prefix}${id.replace(/[.]/g, '-')}`;
}
export function getTokenBasename(token, count = 1) {
    const path = getTokenPath(token);
    let remaining = count;
    let index;
    while(remaining && index !== -1){
        index = path.lastIndexOf('.', index == null ? undefined : index - 1);
        remaining--;
    }
    return index === -1 || index == null ? path : path.slice(index + 1);
}
/**
 * Returns an array of enumerable keys of the token. Works
 * much like Object.keys, but also lists keys that are inherited
 * from $default
 */ export function tokenKeys(obj) {
    const result = [];
    for(const key in obj){
        result.push(key);
    }
    return result;
}
export function getTokenDefaultValue(token) {
    return token[$token].defaultValue;
}
const tokenMethods = Object.create(null);
Object.defineProperties(tokenMethods, {
    toJSON: {
        value () {
            return getTokenId(this);
        },
        enumerable: false
    },
    toString: {
        value () {
            if (!isToken(this)) {
                return '';
            }
            return `${this.valueOf()}`;
        },
        enumerable: false
    },
    valueOf: {
        value () {
            if (!isToken(this)) {
                return undefined;
            }
            return `var(${serializeCustomProperty(getTokenId(this))})`;
        },
        enumerable: false
    }
});
function createTokenObject() {
    const token = Object.create(tokenMethods);
    token[$address] = {};
    return token;
}
/**
 * Creates a new token.
 * Useful for creating tokens with object values.
 */ export function createToken(defaultValue) {
    const token = createTokenObject();
    token[$token] = {
        defaultValue
    };
    return token;
}
const tokenByHash = new Map();
/**
 * Resets the cached map of tokens. Useful when testing.
 */ export function resetTokenCache() {
    tokenByHash.clear();
}
/**
 * Creates new map of tokens. Keeps path separate from options to avoid creating
 * unnecessary objects.
 */ function createTokensImplementation(path, options, input) {
    const isInputToken = isToken(input);
    if ((isInputToken || isTokens(input)) && input[$address].path === path) {
        return input;
    }
    let tokens;
    if (typeof input !== 'object' || !input) {
        tokens = createToken(input);
    } else if (DEFAULT_KEY in input) {
        // If we have a $default property we recursively create that object as
        // tokens first, and then use it as the inherited prototype for the
        // non-default tokens
        const defaultTokens = createTokensImplementation(path, options, input[DEFAULT_KEY]);
        tokens = Object.create(defaultTokens);
        Object.defineProperty(tokens, DEFAULT_KEY, {
            value: defaultTokens,
            enumerable: false
        });
    } else if (isInputToken) {
        tokens = createToken(input);
    } else {
        tokens = createTokenObject();
    }
    // Address the token
    const tokenHash = hash(VERSION + path);
    const id = (process.env.NODE_ENV === 'production' ? '' : `${path}.`) + tokenHash;
    if (isToken(tokens)) {
        // TODO(koop): Consider throwing if the id already exists
        tokenByHash.set(tokenHash, tokens);
    }
    const address = tokens[$address];
    address.id = id;
    address.path = path;
    address.mode = options.mode;
    // Recurse through the tokens input
    if (typeof input === 'object' && !isInputToken) {
        // Mark the tokens as a branch
        tokens[$tokens] = true;
        const inputObj = input;
        const keys = isTokens(inputObj) ? tokenKeys(inputObj) : Object.keys(inputObj);
        keys.forEach((key)=>{
            if (key === DEFAULT_KEY) {
                return;
            }
            tokens[key] = createTokensImplementation(path ? `${path}.${key}` : key, options, input[key]);
        });
    }
    return tokens;
}
const defaultOptions = {
    mode: 'dynamic'
};
/**
 * Creates new map of tokens.
 */ export function createTokens(options, input) {
    var _options_mode;
    return typeof options === 'string' ? createTokensImplementation(options, defaultOptions, input) : createTokensImplementation(options.path, {
        mode: (_options_mode = options === null || options === void 0 ? void 0 : options.mode) !== null && _options_mode !== void 0 ? _options_mode : defaultOptions.mode
    }, input);
}
function resolveTokenCondition(result, isConditionTrue) {
    if (isConditionTrue) {
        const conditions = Object.keys(result);
        for(let i = conditions.length - 1; i >= 0; i--){
            const condition = conditions[i];
            if (isConditionTrue(condition)) {
                return result[condition];
            }
        }
    }
    return result.default;
}
export function resolveTokenShallow(token, overrideMap, isConditionTrue, recordId) {
    if (!isToken(token)) {
        throw new Error('Token cannot be resolved');
    }
    const defaultValue = token[$token].defaultValue;
    let result = defaultValue;
    if (overrideMap) {
        const id = getTokenId(token);
        var _overrideMap_id;
        result = (_overrideMap_id = overrideMap[id]) !== null && _overrideMap_id !== void 0 ? _overrideMap_id : defaultValue;
        if (recordId) {
            recordId(id);
        }
    }
    if (isConditionalObject(result)) {
        var _resolveTokenCondition;
        result = (_resolveTokenCondition = resolveTokenCondition(result, isConditionTrue)) !== null && _resolveTokenCondition !== void 0 ? _resolveTokenCondition : defaultValue;
    }
    return result;
}
/**
 * Returns the value assigned to the input token
 */ export function resolveToken(token, overrideMap, isConditionTrue, recordId) {
    const result = resolveTokenShallow(token, overrideMap, isConditionTrue, recordId);
    return isToken(result) ? resolveToken(result, overrideMap, isConditionTrue, recordId) : result;
}
export function walkTokens(tokens, callback, path = '') {
    callback(tokens, path);
    for(const key in tokens){
        const value = tokens[key];
        walkTokens(value, callback, path === '' ? key : `${path}.${key}`);
    }
}
const digits = /^\d+$/;
function isDigits(str) {
    return !!str.match(digits);
}
export function forEachToken(tokens, callback) {
    walkTokens(tokens, (value, path)=>{
        if (isToken(value)) {
            let key = path;
            if (!path) {
                key = getTokenBasename(value);
            } else if (isDigits(path)) {
                key = getTokenBasename(value, 2);
            }
            callback(value, key);
        }
    });
}
/**
 * Returns a single level key/val object with every token in a set
 * and its provided default value
 */ export function enumerateTokens(tokens) {
    const result = {};
    walkTokens(tokens, (value, path)=>{
        if (isToken(value)) {
            result[path] = resolveToken(value);
        }
    });
    return result;
}
const addressedTokenMaps = [];
export function addressTokens(tokens) {
    if (!addressedTokenMaps.includes(tokens)) {
        addressedTokenMaps.push(tokens);
    }
}
function tokenIdToHash(id) {
    return id.slice(id.lastIndexOf('.') + 1);
}
export function getTokenByHash(tokenHash) {
    return tokenByHash.get(tokenHash);
}
export function getTokenById(id) {
    return getTokenByHash(tokenIdToHash(id));
}
export function getAddressedTokenMaps() {
    return addressedTokenMaps;
}
const TOKEN_VAR_HASH_PATTERN = /var\(--(?:[\w-]*-)?(\w+)\)/g;
const FROZEN_TOKENS = new Set();
export function setStaticToken(token, value) {
    if (getTokenMode(token) !== 'static') {
        throw new Error(`Expected token to be static: "${getTokenPath(token)}"`);
    }
    if (FROZEN_TOKENS.has(token)) {
        throw new Error(`A static token cannot be set after its value is frozen. Token "${getTokenPath(token)}" has already been referenced in another value.`);
    }
    token[$token].defaultValue = value;
}
/**
 * Replaces references to static tokens within a string with their values.
 * When a static token is replaced for the first time, its value is frozen.
 */ export function replaceStaticTokens(value) {
    return value.replace(TOKEN_VAR_HASH_PATTERN, (match, tokenHash)=>{
        const token = getTokenByHash(tokenHash);
        if (token && getTokenMode(token) === 'static') {
            FROZEN_TOKENS.add(token);
            return `${resolveToken(token) || ''}`;
        }
        return match;
    });
}

//# sourceMappingURL=token.js.map